import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import { Canvas, CanvasListResponse, CanvasResponse } from "../canvas/types";
import {
  ArchivedWorkspaceListResponse,
  CreateCanvasInWorkspaceRequest,
  CreateWorkspaceRequest,
  RecentCanvasesByWorkspace,
  RecentCanvasesByWorkspaceResponse,
  UpdateWorkspaceRequest,
  Workspace,
  WorkspaceListResponse,
  WorkspaceResponse,
} from "./types";

export * from "./types";

const ENDPOINTS = {
  LIST: "/api/v2/org/:organization_id/user/:user_id/workspaces",
  CREATE: "/api/v2/org/:organization_id/user/:user_id/workspaces",
  GET: "/api/v2/org/:organization_id/user/:user_id/workspaces/:workspace_id",
  UPDATE: "/api/v2/org/:organization_id/user/:user_id/workspaces/:workspace_id",
  DELETE: "/api/v2/org/:organization_id/user/:user_id/workspaces/:workspace_id",
  RESTORE:
    "/api/v2/org/:organization_id/user/:user_id/workspaces/:workspace_id/restore",
  ARCHIVED: "/api/v2/org/:organization_id/user/:user_id/archived/workspaces",
  LIST_CANVASES:
    "/api/v2/org/:organization_id/user/:user_id/workspaces/:workspace_id/canvases",
  CREATE_CANVAS:
    "/api/v2/org/:organization_id/user/:user_id/workspaces/:workspace_id/canvases",
  RECENT_CANVASES_BY_WORKSPACE:
    "/api/v2/org/:organization_id/user/:user_id/recent-canvases-by-workspace",
};

export class WorkspaceAPI {
  constructor(private client: CohesiveAPIClient) {}

  async list(config?: AxiosRequestConfig): Promise<Workspace[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{
      data: WorkspaceListResponse;
    }>(
      route(ENDPOINTS.LIST, {
        organization_id: organizationId,
        user_id: userId,
      }),
      config,
    );
    return response.data.data.workspaces;
  }

  async create(
    data: CreateWorkspaceRequest,
    config?: AxiosRequestConfig,
  ): Promise<Workspace> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{ data: WorkspaceResponse }>(
      route(ENDPOINTS.CREATE, {
        organization_id: organizationId,
        user_id: userId,
      }),
      data,
      config,
    );
    return response.data.data.workspace;
  }

  async getById(
    workspaceId: string,
    config?: AxiosRequestConfig,
  ): Promise<Workspace> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{ data: WorkspaceResponse }>(
      route(ENDPOINTS.GET, {
        organization_id: organizationId,
        user_id: userId,
        workspace_id: workspaceId,
      }),
      config,
    );
    return response.data.data.workspace;
  }

  async update(
    workspaceId: string,
    data: UpdateWorkspaceRequest,
    config?: AxiosRequestConfig,
  ): Promise<Workspace> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.put<{ data: WorkspaceResponse }>(
      route(ENDPOINTS.UPDATE, {
        organization_id: organizationId,
        user_id: userId,
        workspace_id: workspaceId,
      }),
      data,
      config,
    );
    return response.data.data.workspace;
  }

  async delete(
    workspaceId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    await this.client.axios.delete(
      route(ENDPOINTS.DELETE, {
        organization_id: organizationId,
        user_id: userId,
        workspace_id: workspaceId,
      }),
      config,
    );
  }

  /** Restores a workspace from the archive, along with everything archived with it. */
  async restore(
    workspaceId: string,
    config?: AxiosRequestConfig,
  ): Promise<Workspace> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{ data: WorkspaceResponse }>(
      route(ENDPOINTS.RESTORE, {
        organization_id: organizationId,
        user_id: userId,
        workspace_id: workspaceId,
      }),
      {},
      config,
    );
    return response.data.data.workspace;
  }

  /** Lists archived workspaces, newest first (cursor-paginated by archived_at). */
  async listArchived(
    params?: { limit?: number; before_ts?: string },
    config?: AxiosRequestConfig,
  ): Promise<ArchivedWorkspaceListResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<ArchivedWorkspaceListResponse>(
      route(ENDPOINTS.ARCHIVED, {
        organization_id: organizationId,
        user_id: userId,
      }),
      { ...config, params: { ...config?.params, ...params } },
    );
    return response.data;
  }

  async listCanvases(
    workspaceId: string,
    config?: AxiosRequestConfig,
  ): Promise<Canvas[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{ data: CanvasListResponse }>(
      route(ENDPOINTS.LIST_CANVASES, {
        organization_id: organizationId,
        user_id: userId,
        workspace_id: workspaceId,
      }),
      config,
    );
    return response.data.data.canvases;
  }

  /**
   * Fetches up to the 4 most-recently-modified canvases for every workspace
   * the user can view, in a single request. Returns a map keyed by workspace
   * ID; workspaces with no canvases are omitted by the server.
   */
  async recentCanvasesByWorkspace(
    config?: AxiosRequestConfig,
  ): Promise<RecentCanvasesByWorkspace> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{
      data: RecentCanvasesByWorkspaceResponse;
    }>(
      route(ENDPOINTS.RECENT_CANVASES_BY_WORKSPACE, {
        organization_id: organizationId,
        user_id: userId,
      }),
      config,
    );
    return response.data.data.results;
  }

  async createCanvas(
    workspaceId: string,
    data: CreateCanvasInWorkspaceRequest,
    config?: AxiosRequestConfig,
  ): Promise<Canvas> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{ data: CanvasResponse }>(
      route(ENDPOINTS.CREATE_CANVAS, {
        organization_id: organizationId,
        user_id: userId,
        workspace_id: workspaceId,
      }),
      data,
      config,
    );
    return response.data.data.canvas;
  }
}
